import React, { useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

export default function Popup({ popup, onClose }) {
    const popupRef = useRef(null);
    const { i18n, t } = useTranslation();

    useEffect(() => {
        function handleClickOutside(event) {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                onClose?.();
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    // ✅ Resolve content safely (supports: string OR {he, ar} OR missing)
    const resolvedContent = useMemo(() => {
        if (!popup) return "";

        // support if popup itself is mistakenly a string
        if (typeof popup === "string") return popup;

        const c = popup?.content;

        // if content missing
        if (!c) return "";

        // if object {he, ar}
        if (typeof c === "object") {
            const lang = i18n.language === "ar" ? "ar" : "he";
            return c?.[lang] || c?.he || "";
        }

        // if string
        return String(c);
    }, [popup, i18n.language]);

    const isHtml = !!popup?.isHtml;

    // ✅ translate button + aria label
    const closeLabel = t("popup.close", { defaultValue: "סגור חלון" });
    const okText = t("popup.confirm", { defaultValue: "הבנתי" });

    return (
        <div
            className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center px-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="popup-title"
        >
            <div
                ref={popupRef}
                className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 text-darktext text-right animate-fadeIn leading-relaxed"
            >
                {/* Hidden heading for screen readers */}
                <h2
                    id="popup-title"
                    style={{
                        position: "absolute",
                        width: "1px",
                        height: "1px",
                        padding: 0,
                        margin: "-1px",
                        overflow: "hidden",
                        clip: "rect(0, 0, 0, 0)",
                        whiteSpace: "nowrap",
                        border: 0,
                    }}
                >
                    {t("popup.title", { defaultValue: "הודעת מערכת" })}
                </h2>

                {isHtml ? (
                    <div dangerouslySetInnerHTML={{ __html: resolvedContent }} />
                ) : (
                    <div className="whitespace-pre-line">{resolvedContent}</div>
                )}

                <div className="text-center mt-4">
                    <button
                        onClick={onClose}
                        aria-label={closeLabel}
                        className="cursor-pointer bg-[#3b7c2f] hover:bg-[#2d5f23] text-white font-bold py-2 px-6 rounded transition"
                    >
                        {okText}
                    </button>
                </div>
            </div>
        </div>
    );
}